import React, { useState, useRef, useEffect } from 'react';
import { Button, Modal, ModalContent } from '../common';
import { Heading, Text, Typography } from '../common/Typography';
import { Select } from '../common/Form';
import { updateMatchBestOf } from '../../services/tournamentService';
import { getGenderTextColor } from '../../utils/genderColors';
import {
  validateGameScore,
  getMaxScore,
  getScoringRulesSummary,
  normalizeScoringConfig,
} from '../../utils/gameScoreRules';
import { isSinglesTournament, formatTournamentEntryLabel } from '../../utils/tournamentFormat';
import { handleMobileScoreFocus, markSkipMobileScoreClear, mobileScoreInputAttrs } from '../../utils/mobileScoreInput';

/**
 * TournamentMatchScoreModal Component
 * Modal for recording scores for tournament matches
 * Supports best-of-1, best-of-3, and best-of-5 formats
 * Each match has its own format (match.best_of); hosts can override per-match (e.g. running out of time)
 */
const TournamentMatchScoreModal = ({
  isOpen,
  onClose,
  match,
  tournament,
  onSaveScore,
  onMatchFormatChanged
}) => {
  const [games, setGames] = useState([]);
  const [currentGameIndex, setCurrentGameIndex] = useState(0);
  const [team1Score, setTeam1Score] = useState('');
  const [team2Score, setTeam2Score] = useState('');
  const [scoreError, setScoreError] = useState('');
  const [saving, setSaving] = useState(false);
  const [matchBestOf, setMatchBestOf] = useState(match?.best_of || 1);
  const [changingFormat, setChangingFormat] = useState(false);
  const [editingGameIndex, setEditingGameIndex] = useState(null);

  const team1ScoreInputRef = useRef(null);
  const team2ScoreInputRef = useRef(null);

  // Per-match format (match.best_of) - allows host to override for individual matches
  const bestOf = matchBestOf;

  // Initialize games array based on best_of or existing games (for edit)
  useEffect(() => {
    if (isOpen && match) {
      const gamesNeeded = bestOf;

      if (match.games?.length > 0) {
        // Pre-populate from existing games (edit mode)
        const existingGames = match.games
          .sort((a, b) => (a.game_order ?? 0) - (b.game_order ?? 0))
          .map(g => ({
            team1Score: g.team_1_score,
            team2Score: g.team_2_score,
            winner: g.winning_team
          }));
        const padded = Array(gamesNeeded).fill(null);
        existingGames.forEach((g, i) => { padded[i] = g; });
        setGames(padded);
        const nextIndex = existingGames.length < gamesNeeded ? existingGames.length : gamesNeeded - 1;
        setCurrentGameIndex(nextIndex);
        // Pre-fill score inputs when editing (e.g. best of 1 with 1 game)
        const gameToEdit = padded[nextIndex];
        if (gameToEdit) {
          setTeam1Score(String(gameToEdit.team1Score));
          setTeam2Score(String(gameToEdit.team2Score));
        } else {
          setTeam1Score('');
          setTeam2Score('');
        }
      } else {
        setGames(Array(gamesNeeded).fill(null));
        setCurrentGameIndex(0);
        setTeam1Score('');
        setTeam2Score('');
      }
      setScoreError('');
      setEditingGameIndex(null);
    }
  }, [isOpen, match, bestOf]);

  // Sync matchBestOf when match changes (e.g. after format change)
  useEffect(() => {
    if (isOpen && match) {
      setMatchBestOf(match.best_of || 1);
    }
  }, [isOpen, match?.id, match?.best_of]);

  const gamesPlayed = match?.games?.length ?? 0;
  const handleFormatChange = async (newBestOf) => {
    const value = parseInt(newBestOf, 10);
    if (![1, 3, 5].includes(value) || value === bestOf) return;
    if (gamesPlayed > value) {
      setScoreError(`Cannot change to Best of ${value}: this match already has ${gamesPlayed} game(s) recorded.`);
      return;
    }
    setChangingFormat(true);
    setScoreError(null);
    try {
      await updateMatchBestOf(match.id, value);
      setMatchBestOf(value);
      onMatchFormatChanged?.(match.id);
    } catch (err) {
      setScoreError(err.message || 'Failed to change format');
    } finally {
      setChangingFormat(false);
    }
  };

  if (!isOpen || !match) return null;
  const gamesNeeded = Math.ceil(bestOf / 2); // Games needed to win
  const maxGames = bestOf; // Best of 1 = 1 game, best of 3 = 3 games, etc.

  const scoringConfig = normalizeScoringConfig(tournament);
  const maxScore = getMaxScore(scoringConfig);

  const validateTournamentGameScore = (score1, score2) =>
    validateGameScore(score1, score2, scoringConfig);

  // Validate game sequence: match ends when a team reaches gamesNeeded; no extra games allowed
  const validateGameSequence = (gamesArray) => {
    const arr = gamesArray.filter((g) => g !== null);
    let t1 = 0;
    let t2 = 0;
    for (let i = 0; i < arr.length; i++) {
      if (arr[i].winner === 1) t1++;
      else t2++;
      if (t1 >= gamesNeeded || t2 >= gamesNeeded) {
        if (i < arr.length - 1) {
          return {
            valid: false,
            message: `Invalid: the match would have ended after game ${i + 1}. Remove or edit the extra game(s).`
          };
        }
        return { valid: true };
      }
    }
    return { valid: true };
  };

  // Count wins for each team
  const team1Wins = games.filter(g => g?.winner === 1).length;
  const team2Wins = games.filter(g => g?.winner === 2).length;
  const completedGamesCount = games.filter(g => g !== null).length;

  // Proactive score validation - show warning and disable add when scores are invalid (before submit)
  const bothScoresEntered = team1Score !== '' && team2Score !== '';
  const proactiveScoreError = bothScoresEntered
    ? validateTournamentGameScore(team1Score, team2Score)
    : null;
  const canAddScore = bothScoresEntered && !proactiveScoreError;

  // Check if match is complete
  const isMatchComplete = team1Wins >= gamesNeeded || team2Wins >= gamesNeeded;
  const matchWinner = team1Wins >= gamesNeeded ? 1 : team2Wins >= gamesNeeded ? 2 : null;

  /**
   * Commit current score inputs into a new games array (pure). Used by Add and Save so Save can
   * include a valid pending game — matches the one-tap mental model users expect.
   * @returns {{ error: string|null, games: Array|null }} games=null means nothing to merge (empty inputs)
   */
  const commitPendingInputToGames = () => {
    const isUpdatingExisting = games[currentGameIndex] !== null;
    if (bestOf === 1 && games.some((g) => g !== null) && !isUpdatingExisting) {
      return { error: null, games: null };
    }

    const hasAnyInput = team1Score !== '' || team2Score !== '';
    if (!hasAnyInput) {
      return { error: null, games: null };
    }

    if (team1Score === '' || team2Score === '') {
      return {
        error: 'Enter both scores for this game, or clear both boxes before saving.',
        games: null,
      };
    }

    const score1 = parseInt(team1Score, 10);
    const score2 = parseInt(team2Score, 10);

    if (isNaN(score1) || isNaN(score2)) {
      return { error: 'Please enter valid scores', games: null };
    }
    if (score1 < 0 || score2 < 0) {
      return { error: 'Scores cannot be negative', games: null };
    }
    if (score1 === score2) {
      return { error: 'Scores cannot be equal', games: null };
    }

    const tournamentScoreError = validateTournamentGameScore(score1, score2);
    if (tournamentScoreError) {
      return { error: tournamentScoreError, games: null };
    }

    const winner = score1 > score2 ? 1 : 2;
    const newGames = [...games];
    newGames[currentGameIndex] = {
      team1Score: score1,
      team2Score: score2,
      winner,
    };

    const validation = validateGameSequence(newGames);
    if (!validation.valid) {
      return { error: validation.message, games: null };
    }

    return { error: null, games: newGames };
  };

  // For best of 1, only allow one game - hide score input once we have it
  const canAddMoreScores = !isMatchComplete && completedGamesCount < maxGames;
  // Show input when adding new scores OR when user explicitly clicked Edit on a game
  const showScoreInput = canAddMoreScores || editingGameIndex !== null;

  // Display order (1, 2, 3...) for game labels - reflects sequence when games have gaps from deletions
  const getDisplayOrderForIndex = (idx) =>
    games.slice(0, idx + 1).filter((g) => g !== null).length;

  // Handle score input changes (supports type="number" spinner and manual typing)
  const handleScoreChange = (team, value) => {
    setScoreError(''); // Clear submit errors when user changes scores
    if (value === '' || value === null || value === undefined) {
      if (team === 'team1') setTeam1Score('');
      else setTeam2Score('');
      return;
    }
    const str = String(value).replace(/[^\d]/g, '');
    if (str.length <= 2) {
      // Allow typing up to 30; validation will reject invalid scores (e.g. 22 in no deuce mode)
      const final = str === '' ? '' : String(Math.min(30, Math.max(0, parseInt(str, 10))));
      if (team === 'team1') {
        setTeam1Score(final);
        if (final.length === 2 && team2ScoreInputRef.current) {
          markSkipMobileScoreClear('team2');
          team2ScoreInputRef.current.focus();
        }
      } else {
        setTeam2Score(final);
      }
    }
  };

  // Add game score
  const handleAddGameScore = () => {
    const { error, games: newGames } = commitPendingInputToGames();
    if (error) {
      setScoreError(error);
      return;
    }
    if (!newGames) return;

    setGames(newGames);
    setEditingGameIndex(null);
    setTeam1Score('');
    setTeam2Score('');
    setScoreError('');

    const updatedTeam1Wins = newGames.filter((g) => g?.winner === 1).length;
    const updatedTeam2Wins = newGames.filter((g) => g?.winner === 2).length;
    const updatedIsComplete = updatedTeam1Wins >= gamesNeeded || updatedTeam2Wins >= gamesNeeded;

    if (!updatedIsComplete) {
      setCurrentGameIndex(currentGameIndex + 1);
    }
  };

  // Edit a previous game
  const handleEditGame = (index) => {
    setScoreError('');
    setCurrentGameIndex(index);
    setEditingGameIndex(index);
    const game = games[index];
    if (game) {
      setTeam1Score(game.team1Score.toString());
      setTeam2Score(game.team2Score.toString());
    }
  };

  // Delete a game
  const handleDeleteGame = (index) => {
    const newGames = [...games];
    newGames[index] = null;
    setGames(newGames);
    if (index === editingGameIndex) setEditingGameIndex(null);

    // If deleting current game, clear scores
    if (index === currentGameIndex) {
      setTeam1Score('');
      setTeam2Score('');
    }
  };

  // Save match result (full, partial, or clear - allow 0 games to reset match).
  // If the user entered a valid game in the inputs but forgot to tap "Add game", merge it in here.
  const handleSave = async () => {
    const pending = commitPendingInputToGames();
    if (pending.error) {
      setScoreError(pending.error);
      return;
    }

    const gamesForSave = pending.games ?? games;
    const completedGames = gamesForSave.filter((g) => g !== null);

    if (completedGames.length > 0) {
      const validation = validateGameSequence(gamesForSave);
      if (!validation.valid) {
        setScoreError(validation.message);
        return;
      }
    }

    const t1w = gamesForSave.filter((g) => g?.winner === 1).length;
    const t2w = gamesForSave.filter((g) => g?.winner === 2).length;
    const saveMatchComplete = t1w >= gamesNeeded || t2w >= gamesNeeded;
    const saveMatchWinner = t1w >= gamesNeeded ? 1 : t2w >= gamesNeeded ? 2 : null;

    setSaving(true);
    try {
      const winnerTeamId =
        completedGames.length > 0 && saveMatchComplete
          ? (saveMatchWinner === 1 ? match.entry1_id : match.entry2_id)
          : null;

      await onSaveScore({
        matchId: match.id,
        games: completedGames,
        winnerTeamId,
      });

      onClose();
    } catch (err) {
      console.error('Error saving match score:', err);
      setScoreError('Failed to save score. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const team1 = match.team1 || {};
  const team2 = match.team2 || {};
  const singles = isSinglesTournament(tournament);

  const roundLabel =
    match.round === 'group'
      ? `Group ${match.group?.name || ''}`.trim()
      : (match.round || '').replace(/_/g, ' ');
  const side1Name = formatTournamentEntryLabel(team1, singles);
  const side2Name = formatTournamentEntryLabel(team2, singles);
  const scoringRulesSummary = getScoringRulesSummary(scoringConfig);

  const hasRecordedGames = games.some((g) => g !== null);
  const showReadOnlySummary = isMatchComplete && !showScoreInput && hasRecordedGames;
  const winnerDisplayName = matchWinner === 1 ? side1Name : matchWinner === 2 ? side2Name : null;

  return (
    <Modal isOpen={isOpen} onClose={onClose}>
      <ModalContent size="medium" padding={false} className="flex min-h-0 flex-col overflow-hidden bg-background">
        <div className="min-h-0 flex-1 overflow-y-auto p-4 sm:p-6">
          {/* Title + context in one block */}
          <div className="mb-5 flex items-start justify-between gap-3 border-b border-border-subtle pb-4">
            <div className="min-w-0">
              <Heading level={2} className="mb-1 text-lg sm:text-xl">
                {match.games?.length > 0 ? 'Edit scores' : 'Enter scores'}
              </Heading>
              <Text className="text-text-muted text-sm">
                {[
                  roundLabel,
                  onMatchFormatChanged ? null : `Best of ${bestOf}`,
                  `Games ${team1Wins}–${team2Wins}`,
                ]
                  .filter(Boolean)
                  .join(' · ')}
              </Text>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex min-h-[44px] min-w-[44px] shrink-0 items-center justify-center rounded-lg text-xl leading-none text-text-muted transition-colors hover:bg-surface-subtle hover:text-text-primary"
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {/* Matchup row — hidden while entering scores (names sit above inputs); shown when browsing results */}
          {!showScoreInput && (
            <div className="mb-5 grid grid-cols-[1fr_auto_1fr] items-center gap-2 sm:gap-3">
              <div className="min-w-0 text-right">
                <Text
                  className={`text-sm font-semibold leading-snug sm:text-base ${getGenderTextColor(team1.player1?.gender || '')}`}
                >
                  {side1Name}
                </Text>
              </div>
              <span className="shrink-0 px-1 text-text-muted text-xs font-medium sm:text-sm">vs</span>
              <div className="min-w-0 text-left">
                <Text
                  className={`text-sm font-semibold leading-snug sm:text-base ${getGenderTextColor(team2.player1?.gender || '')}`}
                >
                  {side2Name}
                </Text>
              </div>
            </div>
          )}

          {/* Game scores: merged “match complete” card when finished; lighter list while in progress */}
          {hasRecordedGames && (
            <div className={`mb-5 ${showReadOnlySummary ? 'rounded-xl border border-success/35 bg-success/10 p-4 dark:bg-success/10' : ''}`}>
              {showReadOnlySummary && winnerDisplayName ? (
                <p className="text-center text-sm leading-snug">
                  <span className="font-semibold text-success-dark">{winnerDisplayName}</span>
                  <span className="text-text-secondary"> wins · </span>
                  <span className="tabular-nums text-text-secondary">
                    {team1Wins}–{team2Wins} games
                  </span>
                </p>
              ) : (
                <Text className="mb-2 text-xs font-medium text-text-muted">Games</Text>
              )}
              <ul
                className={
                  showReadOnlySummary
                    ? 'mt-3 divide-y divide-border-subtle/80 rounded-lg border border-border-medium/40 bg-surface/50 dark:bg-surface-elevated/20'
                    : 'divide-y divide-border-subtle/80 rounded-lg border border-border-medium/50 bg-surface-subtle/40'
                }
              >
                {games
                  .map((game, index) => (game ? { game, index } : null))
                  .filter(Boolean)
                  .map(({ game, index }, displayOrder) => (
                    <li
                      key={index}
                      className="flex min-h-[44px] items-center justify-between gap-3 px-3 py-2 text-sm sm:min-h-0 sm:py-2"
                    >
                      <div className="flex min-w-0 items-baseline gap-2">
                        <span className="text-text-muted text-xs">G{displayOrder + 1}</span>
                        <span className="font-semibold tabular-nums text-text-primary">
                          {game.team1Score}–{game.team2Score}
                        </span>
                      </div>
                      <div className="flex shrink-0 gap-1">
                        <button
                          type="button"
                          className="min-h-[44px] min-w-[44px] rounded-md px-2 text-xs font-medium text-primary transition-colors hover:bg-primary/10 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1.5"
                          onClick={() => handleEditGame(index)}
                          aria-label={`Edit game ${displayOrder + 1}`}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="min-h-[44px] min-w-[44px] rounded-md px-2 text-xs font-medium text-danger-dark transition-colors hover:bg-danger-light/30 dark:hover:bg-danger-dark/20 sm:min-h-0 sm:min-w-0 sm:px-2.5 sm:py-1.5"
                          onClick={() => handleDeleteGame(index)}
                          aria-label={`Remove game ${displayOrder + 1}`}
                        >
                          Remove
                        </button>
                      </div>
                    </li>
                  ))}
              </ul>
            </div>
          )}

          {showScoreInput && (
            <div className="mb-5 rounded-xl border border-border-medium/70 bg-surface-subtle/50 p-4 dark:border-white/[0.08] dark:bg-surface-elevated/15">
              <Typography variant="strong" className="mb-1.5 block text-base">
                Game{' '}
                {games[currentGameIndex]
                  ? getDisplayOrderForIndex(currentGameIndex)
                  : completedGamesCount + 1}
              </Typography>

              <details className="mb-3 w-full text-xs text-text-muted">
                <summary className="mb-0 cursor-pointer list-none py-0.5 text-primary hover:text-primary-dark [&::-webkit-details-marker]:hidden">
                  Rules & format
                </summary>
                <div className="mt-1.5 max-w-md space-y-2 border-l-2 border-border-subtle pl-2.5">
                  <p className="m-0 leading-snug">{scoringRulesSummary}</p>
                  {onMatchFormatChanged && (
                    <>
                      <div className="flex flex-wrap items-center gap-2">
                        <Select
                          value={String(bestOf)}
                          onChange={(e) => handleFormatChange(e.target.value)}
                          disabled={changingFormat}
                          options={[
                            { value: '1', label: 'Bo1' },
                            { value: '3', label: 'Bo3' },
                            { value: '5', label: 'Bo5' }
                          ].filter((opt) => gamesPlayed <= parseInt(opt.value, 10))}
                          className="max-w-[100px] py-1 text-xs"
                        />
                        {changingFormat && <span>…</span>}
                      </div>
                      {gamesPlayed > 0 && (
                        <p className="m-0 text-[11px] leading-snug opacity-90">
                          Min. {gamesPlayed} game{gamesPlayed !== 1 ? 's' : ''} (already saved).
                        </p>
                      )}
                    </>
                  )}
                </div>
              </details>

              <div className="grid grid-cols-2 gap-3 sm:gap-5">
                <div>
                  <Text
                    className={`mb-2 block truncate text-center text-xs font-medium sm:text-sm ${getGenderTextColor(team1.player1?.gender || '')}`}
                  >
                    {side1Name}
                  </Text>
                  <input
                    ref={team1ScoreInputRef}
                    value={team1Score}
                    onChange={(e) => handleScoreChange('team1', e.target.value)}
                    onFocus={(e) => handleMobileScoreFocus(e, 'team1', () => setTeam1Score(''))}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0"
                    aria-label={`Points for ${side1Name}`}
                    className="min-h-[56px] w-full rounded-xl border-2 border-border-medium bg-surface px-3 py-3 text-center text-3xl font-bold tabular-nums text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    {...mobileScoreInputAttrs}
                  />
                </div>
                <div>
                  <Text
                    className={`mb-2 block truncate text-center text-xs font-medium sm:text-sm ${getGenderTextColor(team2.player1?.gender || '')}`}
                  >
                    {side2Name}
                  </Text>
                  <input
                    ref={team2ScoreInputRef}
                    value={team2Score}
                    onChange={(e) => handleScoreChange('team2', e.target.value)}
                    onFocus={(e) => handleMobileScoreFocus(e, 'team2', () => setTeam2Score(''))}
                    onWheel={(e) => e.target.blur()}
                    placeholder="0"
                    aria-label={`Points for ${side2Name}`}
                    className="min-h-[56px] w-full rounded-xl border-2 border-border-medium bg-surface px-3 py-3 text-center text-3xl font-bold tabular-nums text-text-primary focus:outline-none focus:ring-2 focus:ring-primary"
                    {...mobileScoreInputAttrs}
                  />
                </div>
              </div>

              {(proactiveScoreError || scoreError) && (
                <div
                  role="alert"
                  className="mt-4 rounded-lg border border-danger/30 bg-danger-light/35 px-3 py-2.5 text-center dark:bg-danger-dark/15"
                >
                  <Text className="text-sm text-danger">{proactiveScoreError || scoreError}</Text>
                </div>
              )}

              <Button
                variant={canAddScore ? 'success' : 'secondary'}
                size="medium"
                onClick={handleAddGameScore}
                disabled={!canAddScore}
                className="mt-4 w-full"
              >
                {games[currentGameIndex]
                  ? `Update game ${getDisplayOrderForIndex(currentGameIndex)}`
                  : `Add game ${completedGamesCount + 1}`}
              </Button>
            </div>
          )}

          <div className="flex flex-col gap-2 border-t border-border-subtle pt-4 sm:flex-row sm:items-center sm:justify-between">
            <Button variant="secondary" size="medium" onClick={onClose} className="sm:min-w-[100px]">
              Cancel
            </Button>
            <Button
              variant={isMatchComplete ? 'success' : 'primary'}
              size="medium"
              onClick={handleSave}
              disabled={saving}
              className="sm:min-w-[160px] sm:flex-1 sm:max-w-[280px]"
            >
              {saving
                ? 'Saving…'
                : completedGamesCount === 0 && !canAddScore
                  ? 'Clear match'
                  : isMatchComplete
                    ? 'Save & close'
                    : 'Save progress'}
            </Button>
          </div>
        </div>
      </ModalContent>
    </Modal>
  );
};

export default TournamentMatchScoreModal;
